import { compareDesc } from 'date-fns'
import { Color } from '@opallabs/gem'
import { getNestedProperty } from '@opallabs/lambda'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import {
  Assignment,
  ContextCollection,
  MomentCollection,
  ResponseCollection,
  ResponseRelationship,
  StageCollection,
} from '../../../types'
import { NormalizedTask } from './types'

type AssignmentToTaskMapper = (arg: {
  assignments: Assignment[]
  assignmentsContextMap: ContextCollection
  moments: MomentCollection
  responses: ResponseCollection
  stages: StageCollection
}) => NormalizedTask[]

export const normalizeAssignmentsToTasks: AssignmentToTaskMapper = ({
  assignments,
  assignmentsContextMap,
  moments,
  responses,
  stages,
}) =>
  assignments.map((assignment) => {
    const normalizedTask: NormalizedTask = {
      assignmentType: 'task',
      checked: false,
      checkpointableDate: new Date(assignment.created_at),
      completedAt: null,
      contextName: '',
      disableCheckbox: false,
      entityType: 'assignment',
      hardDueDate: false,
      iconColor: Color.light.Text.Gray,
      iconName: 'moment',
      id: assignment.id,
      phaseIsComplete: false,
      phaseIsCurrent: false,
      starrable: true,
      starred: false,
      taskStatus: AssignmentStatus.Default,
      taskText: '',
    }

    const assignmentMetadata = assignment.metadata
    if (assignmentMetadata !== undefined) {
      normalizedTask.taskText =
        assignmentMetadata && assignmentMetadata.title
          ? assignmentMetadata.title
          : 'Task'
      normalizedTask.starred =
        assignmentMetadata && assignmentMetadata.is_starred
          ? assignmentMetadata.is_starred
          : false
    }

    const stageId = getNestedProperty<string>(
      assignment,
      'relationships',
      'stage',
      'id'
    )
    if (stageId !== undefined) {
      const stage = stages[stageId]
      if (stage !== undefined) {
        normalizedTask.phaseName =
          stage.metadata && stage.metadata.title
            ? stage.metadata.title
            : 'Task Phase'
        normalizedTask.phaseIsComplete = stage.status
          ? stage.status === 'complete'
          : false
        normalizedTask.phaseIsCurrent = stage.status
          ? stage.status === 'active'
          : false
        if (stage.due_date !== null) {
          // If the stage has a due date, set normalizedTask.dueDate
          // this also means that hardDueDate should be set to true
          normalizedTask.dueDate = new Date(stage.due_date)
          normalizedTask.hardDueDate = true
        }
      }

      const context = assignmentsContextMap[assignment.id]
      const momentId = getNestedProperty<string>(
        context,
        'relationships',
        'moment',
        'id'
      )
      if (momentId !== undefined) {
        normalizedTask.momentId = momentId
        const moment = moments[momentId]
        if (moment !== undefined) {
          if (normalizedTask.dueDate === undefined && moment.scheduled_at) {
            // If normalizedTask.dueDate was not set via stage, fall back to Moment.scheduled_at
            normalizedTask.dueDate = new Date(moment.scheduled_at)
          }
          normalizedTask.contextName = moment.title
        }
      }
    }

    const responseRelationship = getNestedProperty<
      ResponseRelationship | ResponseRelationship[]
    >(assignment, 'relationships', 'responses')
    if (responseRelationship !== undefined) {
      if (
        !(
          Array.isArray(responseRelationship) &&
          responseRelationship.length === 0
        )
      ) {
        let responseId: string
        if (Array.isArray(responseRelationship)) {
          const orderedResponses = Object.values(responses)
            .filter(
              (response) =>
                response.relationships.assignment.id === assignment.id
            )
            .sort((a, b) =>
              compareDesc(new Date(a.created_at), new Date(b.created_at))
            )
          responseId = orderedResponses[0].id
        } else {
          responseId = responseRelationship.id
        }
        const response = responses[responseId]
        normalizedTask.checked = response.details.complete
          ? response.details.complete
          : false
        if (response.details.complete) {
          normalizedTask.completedAt = new Date(assignment.updated_at)
        }
      }
    }

    normalizedTask.starrable = !normalizedTask.phaseIsComplete

    normalizedTask.disableCheckbox =
      (normalizedTask.checked && normalizedTask.phaseIsComplete) ||
      (!normalizedTask.phaseIsCurrent && !normalizedTask.phaseIsComplete)

    return normalizedTask
  })
